// src/views/CustomizeView.jsx
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Emoji from "../components/Emoji";
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
import DigitalWallBackground from "../components/DigitalWallBackground";
import { trackEvent, trackPurchaseClick } from "../utils/Analytics";
import BackgroundLights from "./EmpathyAI/components/BackgroundLights";

import HeroBackgroundImage from "../images/bg-deck.png";
import HeroBackgroundImageWebp from "../images/bg-deck.webp";
import HeroDeckImage from "../images/mock-deck.png";
import HeroDeckImageWebp from "../images/mock-deck.webp";
import LettuceLogo from "../images/logo.png";
import LettuceLogoWebp from "../images/logo.webp";
import CardImage from "../images/card-sample.png";
import CardImageWebp from "../images/card-sample.webp";
import TestimonialImage from "../images/linda.png";
import TestimonialImageWebp from "../images/linda.webp";
import FamilyImage from "../images/myfamily.png";
import FamilyImageWebp from "../images/myfamily.webp";
import ClarenceImage from "../images/kong.png";
import ClarenceImageWebp from "../images/kong.webp";
import AlyssaImage from "../images/alyssa.png";
import AlyssaImageWebp from "../images/alyssa.webp";
import AppImage from "../images/app-mockup.png";
import AppImageWebp from "../images/app-mockup.webp";
import McaadImage from "../images/mcaad.png";
import McaadImageWebp from "../images/mcaad.webp";
import McaadDecksImage from "../images/mcaad-decks.png";
import McaadDecksImageWebp from "../images/mcaad-decks.webp";

const MAINTENANCE_MODE = false; // Set to true to hide all GoogleSignInButton components
const HOME_PAGE_URL = "https://lettucetalk.imoonshots.com/";
const HOME_PAGE_TITLE = "LettuceTalk | Caregiving Conversation Cards for Families";
const HOME_PAGE_DESCRIPTION = "Start hard caregiving conversations with gentle LettuceTalk cards in English, Korean, Spanish, and Simplified Chinese—plus optional AI personalization.";
const HOME_PAGE_IMAGE_URL = "https://lettucetalk.imoonshots.com/social-image.png";
const HOME_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: HOME_PAGE_URL,
  name: HOME_PAGE_TITLE,
  description: HOME_PAGE_DESCRIPTION,
  primaryImageOfPage: HOME_PAGE_IMAGE_URL,
};

function LandingImage({
  src,
  webpSrc,
  alt,
  width,
  height,
  className,
  pictureClassName,
  loading = "lazy",
  fetchPriority,
}) {
  return (
    <picture className={pictureClassName}>
      <source type="image/webp" srcSet={webpSrc} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        fetchpriority={fetchPriority}
        className={className}
      />
    </picture>
  );
}

function LandingReveal({ as: Component = "div", children, className = "", delay = 0, ...props }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setIsVisible(true);
        observer.unobserve(element);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <Component
      ref={ref}
      className={`landing-reveal ${isVisible ? "landing-reveal--visible" : ""} ${className}`.trim()}
      style={{ "--landing-reveal-delay": `${delay}ms` }}
      {...props}
    >
      {children}
    </Component>
  );
}

function WebLandingView() {
  const navigate = useNavigate();

  const handleCustomizeClick = () => {
    trackEvent("CTA", "Create a Free Card", "Web Landing");
    navigate("/refine");
  };

  return (
    <>
      <Helmet>
        <title>{HOME_PAGE_TITLE}</title>
        <meta name="description" content={HOME_PAGE_DESCRIPTION} />
        <meta name="robots" content="max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={HOME_PAGE_URL} />
        <meta property="og:title" content={HOME_PAGE_TITLE} />
        <meta property="og:description" content={HOME_PAGE_DESCRIPTION} />
        <meta property="og:image" content={HOME_PAGE_IMAGE_URL} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={HOME_PAGE_URL} />
        <meta property="twitter:title" content={HOME_PAGE_TITLE} />
        <meta property="twitter:description" content={HOME_PAGE_DESCRIPTION} />
        <meta property="twitter:image" content={HOME_PAGE_IMAGE_URL} />
        <link rel="canonical" href={HOME_PAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(HOME_PAGE_SCHEMA)}</script>
      </Helmet>

      <main className="bg-[#FFF9E6] min-h-screen flex flex-col items-center overflow-x-hidden">
        {/* Hero Section */}
        <header className="relative w-full overflow-hidden bg-[#fff8e8] px-4 pb-12 pt-5 sm:px-6 lg:min-h-[820px] lg:px-8 lg:pb-20">
          <div
            className="absolute inset-x-0 top-0 h-[430px] sm:h-[540px] lg:inset-0 lg:h-full"
            aria-hidden="true"
          >
            <LandingImage
              src={HeroBackgroundImage}
              webpSrc={HeroBackgroundImageWebp}
              alt=""
              width={1672}
              height={941}
              loading="eager"
              fetchPriority="high"
              pictureClassName="block h-full w-full"
              className="h-full w-full object-cover object-center"
            />
          </div>
          <LandingReveal
            className="pointer-events-none absolute left-1/2 top-[112px] z-10 w-[168px] -translate-x-1/2 sm:top-[132px] sm:w-[208px] lg:left-[29%] lg:top-[188px] lg:w-[286px] xl:left-[28%] xl:w-[300px]"
            delay={100}
            aria-hidden="true"
          >
            <div className="absolute bottom-[-2px] left-1/2 z-0 h-4 w-[74%] -translate-x-1/2 rounded-full bg-[#4a3316]/42 blur-md sm:h-5 lg:h-6 lg:bg-[#4a3316]/38" />
            <div className="absolute bottom-[-12px] left-1/2 z-0 h-10 w-[96%] -translate-x-1/2 rounded-full bg-[#8b5d25]/24 blur-2xl sm:h-12 lg:bottom-[-16px] lg:h-16 lg:w-[108%] lg:bg-[#8b5d25]/22" />
            <div className="absolute bottom-[10px] left-[52%] z-0 h-8 w-[58%] -translate-x-1/2 rounded-full bg-black/18 blur-xl lg:h-10" />
            <LandingImage
              src={HeroDeckImage}
              webpSrc={HeroDeckImageWebp}
              alt=""
              width={676}
              height={1430}
              loading="eager"
              fetchPriority="high"
              className="relative z-10 w-full drop-shadow-[0_14px_18px_rgba(64,41,13,0.2)] lg:drop-shadow-[0_24px_28px_rgba(64,41,13,0.2)]"
            />
          </LandingReveal>
          <LandingReveal as="nav" className="relative z-20 mx-auto flex max-w-7xl items-center justify-between gap-5" delay={50}>
            <a href="/" className="flex items-center gap-2 no-underline" aria-label="LettuceTalk home">
              <LandingImage
                src={LettuceLogo}
                webpSrc={LettuceLogoWebp}
                alt=""
                width={494}
                height={570}
                loading="eager"
                className="h-11 w-11 object-contain drop-shadow-sm"
              />
              <span className="font-title text-2xl font-bold text-[#245f07] sm:text-3xl">LettuceTalk</span>
            </a>

            {/* <div className="hidden items-center gap-8 text-sm font-bold text-[#17210f] lg:flex">
              <a href="#inside" className="transition hover:text-[#307609]">How It Works</a>
              <a href="#multilingual" className="transition hover:text-[#307609]">For Families</a>
              <a href="#ai-tools" className="transition hover:text-[#307609]">For Professionals</a>
              <a href="#story" className="transition hover:text-[#307609]">About</a>
            </div> */}

            <PrimaryButton
              className="hidden rounded-full px-6 shadow-[0_10px_22px_rgba(48,118,9,0.24)] sm:inline-flex"
              to="/refine"
              icon="heart"
              onClick={() => trackEvent("CTA", "Try LettuceTalk Free Navbar", "Web Landing")}
            >
              Try LettuceTalk Free
            </PrimaryButton>
          </LandingReveal>

          <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-8 pt-[430px] sm:pt-[540px] lg:grid-cols-[0.92fr_1.08fr] lg:pt-24">
            <div className="hidden lg:block" aria-hidden="true" />

            <LandingReveal className="mx-auto max-w-2xl rounded-[2rem] bg-[#fff8e8]/95 px-5 py-7 text-center shadow-[0_18px_44px_rgba(78,67,24,0.12)] sm:px-8 sm:py-9 lg:mx-0 lg:ml-auto lg:bg-transparent lg:p-0 lg:text-left lg:shadow-none" delay={180}>
              <h1 className="font-serif text-4xl font-bold leading-[0.98] tracking-[-0.035em] text-[#082510] sm:text-5xl md:text-6xl lg:text-6xl">
                A gentle way to talk with aging parents about care.
              </h1>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-[#2f352b] sm:mt-7 sm:text-xl sm:leading-8">
                LettuceTalk helps families start difficult caregiving conversations through multilingual conversation cards and a personalized digital experience.
              </p>
              <div className="mt-7 flex flex-col items-stretch gap-3 sm:mt-8 sm:flex-row sm:items-center sm:justify-center lg:justify-start">
                <PrimaryButton
                  isLarge
                  className="w-full rounded-full px-8 shadow-[0_16px_30px_rgba(48,118,9,0.28)] sm:w-auto sm:px-10"
                  to="/refine"
                  icon="heart"
                  onClick={() => trackEvent("CTA", "Try LettuceTalk Free Hero", "Web Landing")}
                >
                  Try LettuceTalk Free
                </PrimaryButton>
                <SecondaryButton
                  isLarge
                  className="w-full px-8 sm:w-auto sm:px-10"
                  href="https://buy.stripe.com/8x2bJ17g71w40Ut4aifQI05"
                  icon="order"
                  onClick={() => {
                    trackEvent("Order", "Order Clicked", "User clicked order button");
                    trackPurchaseClick({
                      surface: "web_landing",
                      placement: "hero",
                    });
                  }}
                >
                  Order the Deck
                </SecondaryButton>
              </div>
              {/* TODO: Implement wholesale/bulk order contact information */}
              {/* <p className="mt-6 flex items-center justify-center gap-1 text-sm font-medium text-[#2f352b] lg:justify-start">
                For wholesale or bulk orders, please email <a href="mailto:hello@imoonshots.com" className="text-[#307609] underline hover:no-underline">hello@imoonshots.com</a>
              </p> */}

              {MAINTENANCE_MODE && (
                <p className="mt-4 max-w-xl rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-900">
                  LettuceTalk's AI app is currently under maintenance. We're doing some light pruning and will be back in a crunch!
                </p>
              )}
            </LandingReveal>
          </div>

        </header>

        <section aria-label="LettuceTalk highlights" className="relative z-30 w-full bg-gradient-to-b from-[#FFF9E6] to-[#F4F8EC] px-4 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4 md:pb-6 md:pt-6">
          <LandingReveal className="mx-auto max-w-6xl" delay={80}>
            <div className="grid overflow-hidden rounded-2xl border border-[#E4EAD8] bg-white/90 shadow-[0_10px_28px_rgba(78,67,24,0.07)] sm:grid-cols-3">
              <div className="flex gap-4 border-b border-[#E4EAD8] p-5 sm:border-b-0 sm:border-r lg:p-6">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#F4F8EC] text-[#307609]" aria-hidden="true">
                  <svg
                    aria-hidden="true"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 11.25c0 4.97-3.69 8.25-8.25 8.25a8.8 8.8 0 0 1-3.69-.8L4 20.25l1.55-3.7a8.22 8.22 0 0 1-1.8-5.3c0-4.97 3.69-9 8.25-9s8.25 4.03 8.25 9Z" />
                  </svg>
                </span>
                <div>
                  <p className="font-bold text-[#17210f]">50 conversation prompts</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600">Ready-made questions for care, aging, values, finances, and family.</p>
                </div>
              </div>
              <div className="flex gap-4 border-b border-[#E4EAD8] p-5 sm:border-b-0 sm:border-r lg:p-6">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#FFF7D6] text-[#9B7400]" aria-hidden="true">
                  <svg
                    aria-hidden="true"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <circle cx="12" cy="12" r="8.75" />
                    <path strokeLinecap="round" d="M3.25 12h17.5M12 3.25c2.1 2.34 3.25 5.28 3.25 8.75S14.1 18.41 12 20.75c-2.1-2.34-3.25-5.28-3.25-8.75S9.9 5.59 12 3.25Z" />
                  </svg>
                </span>
                <div>
                  <p className="font-bold text-[#17210f]">4 languages</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600">English, Korean, Spanish, and Simplified Chinese in every deck.</p>
                </div>
              </div>
              <div className="flex gap-4 p-5 lg:p-6">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#F4F8EC] text-xl text-[#307609]" aria-hidden="true">✧</span>
                <div>
                  <p className="font-bold text-[#17210f]">Personalize for your family</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600">Turn your own hard-to-say thought into three gentle ways to ask using our Empathy AI system.</p>
                </div>
              </div>
            </div>
          </LandingReveal>
        </section>

        {/* Tailor Section */}
        <section id="ai-tools" aria-label="ai-features" className="relative isolate w-full overflow-hidden bg-[#050604] px-4 py-12 sm:px-6 md:py-16">
          <BackgroundLights showFieldAura={false} wide />
          <LandingReveal className="relative z-10 mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="w-full lg:w-1/2 flex flex-col items-center lg:items-start">
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">
                  Turn a hard thought into a gentle card.
                </h2>
                <p className="text-white/75 text-lg mb-8">
                  Start with the question, worry, or thought you wish you could say. LettuceTalk’s Digital Experience creates three gentle options, with an optional translation, that you can keep or share.
                </p>
                <div className="flex flex-row flex-wrap items-center justify-center gap-3 mb-6 lg:justify-start">
                  <PrimaryButton
                    type="button"
                    onClick={handleCustomizeClick}
                  >
                    Create a Free Card
                  </PrimaryButton>
                  <SecondaryButton
                    to="/wall/community"
                    variant="darkSecondary"
                    className="border-white/40 bg-white/10 text-white hover:bg-white/15"
                    onClick={() => trackEvent("CTA", "Explore Community Wall", "Web Landing")}
                  >
                    Explore Community Wall
                  </SecondaryButton>
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <LandingImage
                  src={AppImage}
                  webpSrc={AppImageWebp}
                  alt="LettuceTalk digital caregiving card with a gentle question about checking in on a parent's driving"
                  width={1500}
                  height={1500}
                  className="w-full rounded-3xl max-w-md mx-auto"
                />
              </div>
            </div>
          </LandingReveal>
        </section>

        <section
          aria-labelledby="recognition-title"
          className="relative w-full overflow-hidden border-y border-white/10 bg-[#0b1011] px-4 py-12 text-[#f6f2ea] sm:px-6 md:py-16"
        >
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-[58%] sm:w-[60%]"
            aria-hidden="true"
          >
            <LandingImage
              src={McaadImage}
              webpSrc={McaadImageWebp}
              alt=""
              width={1764}
              height={1048}
              pictureClassName="block h-full w-full"
              className="h-full w-full object-cover object-center"
            />
          </div>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #050c0e 0%, #050c0e 49%, rgba(5, 12, 14, 0.78) 58%, rgba(5, 12, 14, 0.48) 72%, rgba(5, 12, 14, 0.18) 88%, rgba(5, 12, 14, 0.04) 100%), linear-gradient(0deg, rgba(5, 12, 14, 0.24), transparent 55%)",
            }}
            aria-hidden="true"
          />
          <LandingReveal className="relative mx-auto flex max-w-7xl flex-col items-center gap-8 text-center sm:flex-row sm:items-center sm:gap-10 sm:text-left" delay={80}>
            <div className="w-full max-w-xs sm:w-[32%] sm:flex-shrink-0 lg:w-[28%]">
              <LandingImage
                src={McaadDecksImage}
                webpSrc={McaadDecksImageWebp}
                alt="LettuceTalk deck display at the Milken Center for Advancing the American Dream"
                width={655}
                height={599}
                className="w-full rounded-[0.35rem] shadow-[0_1.5rem_3.5rem_rgba(0,0,0,0.58),0_0.35rem_1rem_rgba(0,0,0,0.32)]"
              />
            </div>
            <div className="max-w-2xl">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-[#b98b3f]">
                Museum Launch
              </p>
              <h2 id="recognition-title" className="font-serif text-4xl font-normal leading-none tracking-[-0.035em] text-[#f7f2e9] sm:text-5xl">
                SOLD OUT
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-white/75 sm:text-lg">
                LettuceTalk's initial inventory at the Milken Center for Advancing the American Dream in Washington, DC
              </p>
            </div>
          </LandingReveal>
        </section>

        {/* Testimonial Section */}
        <section aria-label="testimonials" className="w-full border-b border-gray-100 bg-[#F4F8EC] px-4 py-16 sm:px-6 md:py-24">
          <LandingReveal className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#245f07] text-center mb-8 flex items-center justify-center gap-2">
              <Emoji symbol="⭐" label="star" />
              Trusted by Families & Professionals
              <Emoji symbol="⭐" label="star" />
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-10 max-w-6xl mx-auto">
              {/* Linda Testimonial */}
              <div className="flex flex-col items-center text-center max-w-md bg-white rounded-xl shadow p-6 flex-1">
                <LandingImage
                  src={TestimonialImage}
                  webpSrc={TestimonialImageWebp}
                  alt="Linda, LettuceTalk user"
                  width={189}
                  height={174}
                  className="w-28 h-28 rounded-full mb-4 object-cover"
                />
                <blockquote className="italic text-[#3F3F46] mb-4">
                  "I first tried it because my daughter made it, but I was surprised at how LettuceTalk opened up some of the deepest, most heartfelt conversations we've ever had as a family. We started with laughter and ended in tears, realizing just how much we love and care for each other. These cards hold a surprising, powerful kind of love.”
                </blockquote>
                <span className="font-bold text-[#4D7C0F] text-lg block">— Linda</span>
                <span className="text-[#6B7280] text-base font-medium mt-1">Korean-American Senior, Founder's Mom, 58</span>
              </div>

              {/* Clarence Testimonial */}
              <div className="flex flex-col items-center text-center max-w-md bg-white rounded-xl shadow p-6 flex-1">
                <LandingImage
                  src={ClarenceImage}
                  webpSrc={ClarenceImageWebp}
                  alt="Dr. Clarence Kong, LettuceTalk user"
                  width={495}
                  height={495}
                  className="w-28 h-28 rounded-full mb-4 object-cover"
                />
                <blockquote className="italic text-[#3F3F46] mb-4">
                  "As a physician, I’ve had many conversations with my patients about care and end-of-life decisions. LettuceTalk is a truly meaningful, practical way for families to continue those conversations with compassion after their discharge from the emergency department."
                </blockquote>
                <span className="font-bold text-[#4D7C0F] text-lg block">— Dr. Clarence Kong, MD, NREMT</span>
                <span className="text-[#6B7280] text-base font-medium mt-1">Bon Secours Maryview-Harborview Emergency Department, VA</span>
              </div>

              {/* Alyssa Testimonial */}
              <div className="flex flex-col items-center text-center max-w-md bg-white rounded-xl shadow p-6 flex-1">
                <LandingImage
                  src={AlyssaImage}
                  webpSrc={AlyssaImageWebp}
                  alt="LettuceTalk deck mockup"
                  width={1024}
                  height={1024}
                  className="w-28 h-28 rounded-full mb-4 object-cover"
                />
                <blockquote className="italic text-[#3F3F46] mb-4">
                  "After my dad had a sudden stroke, Mandarin came back to him first. LettuceTalk became a fun way to support his language recovery and understand his needs better. I wish I had these sooner. I’ll be recommending them to friends from immigrant families—they make difficult conversations feel light, thoughtful, and full of care."
                </blockquote>
                <span className="font-bold text-[#4D7C0F] text-lg block">— Alyssa</span>
                <span className="text-[#6B7280] text-base font-medium mt-1">Taiwanese-American Daughter, 28</span>
              </div>
            </div>
          </LandingReveal>
        </section>

        {/* Story Section */}
        <section id="story" aria-label="about" className="w-full bg-gradient-to-b from-[#FFF9E6] to-white px-4 py-16 sm:px-6 md:py-24">
          <LandingReveal className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="w-full lg:w-1/2">
                <LandingImage
                  src={FamilyImage}
                  webpSrc={FamilyImageWebp}
                  alt="LettuceTalk cards preview showing bilingual conversation prompts"
                  width={980}
                  height={960}
                  className="w-full max-w-md mx-auto"
                />
              </div>
              <div className="w-full lg:w-1/2 flex flex-col items-center lg:items-start">
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#245f07] mb-6">
                  LettuceTalk's Story
                </h2>
                <p className="text-[#3F3F46] text-lg mb-8">
                  As a daughter, I struggled to ask my Korean mom about her care. Health scares, cultural stigma, and language barriers made the conversations feel impossible. I built LettuceTalk to give families like mine a gentle, practical way to talk—with love, respect, and understanding.
                </p>
                <div className="w-full text-center lg:text-left">
                  <p className="font-serif text-xl italic text-[#245f07]">— Michelle Park</p>
                  <p className="mt-1 text-sm font-bold tracking-wide text-[#3F3F46]">Founder &amp; CEO</p>
                </div>
              </div>

            </div>
          </LandingReveal>
        </section>

        {/* Problems Section */}
        <section aria-label="features" className="w-full bg-gradient-to-b from-[#FFF9E6] to-white px-4 py-16 sm:px-6 md:py-24">
          <LandingReveal className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="order-last w-full lg:order-first lg:w-1/2 flex flex-col items-center lg:items-start">
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#245f07] mb-6">
                  Need a gentle start in person?
                </h2>
                <p className="text-[#3F3F46] text-lg mb-8">
                  50 ready-made conversation prompts in 4 languages. Topics include caregiving, values, family memories, independence, finances, communication styles, and end-of-life wishes. Designed for multilingual and multigenerational families.
                </p>
                <div className="grid w-full grid-cols-2 gap-x-4 gap-y-8 rounded-2xl border border-[#E4EAD8] bg-white p-5 shadow-[0_10px_28px_rgba(78,67,24,0.07)] sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <svg aria-hidden="true" className="h-10 w-10 text-[#6F8F5C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="7" r="2.5" />
                      <circle cx="5.5" cy="9" r="2" />
                      <circle cx="18.5" cy="9" r="2" />
                      <path strokeLinecap="round" d="M7 19v-2a5 5 0 0 1 10 0v2M2.5 18v-1.25A3.75 3.75 0 0 1 6.25 13h.5M21.5 18v-1.25A3.75 3.75 0 0 0 17.75 13h-.5" />
                    </svg>
                    <span className="text-xs font-bold text-[#2f352b] sm:text-sm">Families</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <svg aria-hidden="true" className="h-10 w-10 text-[#6F8F5C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 20s-7-4.35-7-9.3A3.7 3.7 0 0 1 12 8.5a3.7 3.7 0 0 1 7 2.2C19 15.65 12 20 12 20Z" />
                      <path strokeLinecap="round" d="M5 18.5c.9-1.65 2.2-2.5 3.9-2.5h1.6M19 18.5c-.9-1.65-2.2-2.5-3.9-2.5h-1.6" />
                    </svg>
                    <span className="text-xs font-bold text-[#2f352b] sm:text-sm">Caregivers</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <svg aria-hidden="true" className="h-10 w-10 text-[#6F8F5C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="7" r="2.5" />
                      <circle cx="5.5" cy="9" r="2" />
                      <circle cx="18.5" cy="9" r="2" />
                      <path strokeLinecap="round" d="M7 19v-2a5 5 0 0 1 10 0v2M2.5 18v-1.25A3.75 3.75 0 0 1 6.25 13h.5M21.5 18v-1.25A3.75 3.75 0 0 0 17.75 13h-.5" />
                    </svg>
                    <span className="text-xs font-bold text-[#2f352b] sm:text-sm">Adult Children</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <svg aria-hidden="true" className="h-10 w-10 text-[#6F8F5C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <rect x="4" y="7.5" width="16" height="12" rx="2" />
                      <path strokeLinecap="round" d="M9 7.5V5.75A1.75 1.75 0 0 1 10.75 4h2.5A1.75 1.75 0 0 1 15 5.75V7.5M4 12h16M10.25 12v2h3.5v-2" />
                    </svg>
                    <span className="text-xs font-bold text-[#2f352b] sm:text-sm">Professionals</span>
                  </div>
                </div>
                <div className="mt-10 flex w-full flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-10 lg:justify-start">
                  <PrimaryButton
                    href="https://buy.stripe.com/8x2bJ17g71w40Ut4aifQI05"
                    icon="order"
                    onClick={() => {
                      trackEvent("Order", "Order Clicked", "User clicked order button");
                      trackPurchaseClick({
                        surface: "web_landing",
                        placement: "prefer_table_section",
                      });
                    }}
                  >
                    Order the Deck
                  </PrimaryButton>
                  <div className="flex items-center gap-2 text-left text-xs leading-4 text-[#245f07]">
                    <svg aria-hidden="true" className="h-7 w-7 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6.5h11v9H3zM14 10h3l3 3v2.5h-6z" />
                      <circle cx="7" cy="17.5" r="1.5" />
                      <circle cx="18" cy="17.5" r="1.5" />
                      <path strokeLinecap="round" d="M3 15.5h2M20 15.5h1" />
                    </svg>
                    <span>
                      <span className="block whitespace-nowrap font-bold">Free shipping within the U.S.</span>
                      <span className="mt-1 block whitespace-nowrap text-[10px]">International shipping coming soon!</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="order-first w-full lg:order-last lg:w-1/2">
                <LandingImage
                  src={CardImage}
                  webpSrc={CardImageWebp}
                  alt="LettuceTalk cards preview showing bilingual conversation prompts"
                  width={983}
                  height={976}
                  className="w-full max-w-md mx-auto rounded-3xl"
                />
              </div>
            </div>
          </LandingReveal>
        </section>

        <section
          aria-label="conversation CTA"
          className="w-full bg-[#245f07] px-4 py-8 sm:px-6 md:py-10"
        >
          <LandingReveal className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
            <div className="lg:flex-1">
              <h2 className="font-serif text-3xl font-bold leading-tight text-white sm:text-4xl">
                Questions to bring us closer.
              </h2>
            </div>

            <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-[58%]">
              <Link
                to="/refine"
                className="group flex min-h-[96px] items-center gap-4 rounded-2xl bg-white px-4 py-3 text-left shadow-[0_12px_26px_rgba(8,37,16,0.18)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(8,37,16,0.24)] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#245f07]"
                onClick={() => trackEvent("CTA", "Try LettuceTalk Free Bottom Banner", "Web Landing")}
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#F4F8EC] text-[#245f07]" aria-hidden="true">
                  <svg aria-hidden="true" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.8 8.8c0 5.5-8.8 10.3-8.8 10.3S3.2 14.3 3.2 8.8A4.8 4.8 0 0 1 12 6.2a4.8 4.8 0 0 1 8.8 2.6Z" />
                  </svg>
                </span>
                <span>
                  <span className="block font-bold text-[#17210f]">Try LettuceTalk Free</span>
                  <span className="mt-1 block text-xs leading-5 text-[#4d5749]">Create gentle cards in seconds with our AI experience.</span>
                </span>
              </Link>

              <a
                href="https://buy.stripe.com/8x2bJ17g71w40Ut4aifQI05"
                className="group flex min-h-[96px] items-center gap-4 rounded-2xl border border-white/80 bg-white/10 px-4 py-3 text-left text-white transition hover:-translate-y-0.5 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#245f07]"
                onClick={() => {
                  trackEvent("Order", "Order Clicked", "User clicked order button");
                  trackPurchaseClick({
                    surface: "web_landing",
                    placement: "bottom_banner",
                  });
                }}
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-white" aria-hidden="true">
                  <svg aria-hidden="true" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4 7.5 8 4.5 8-4.5M12 12v9" />
                  </svg>
                </span>
                <span>
                  <span className="block font-bold">Order the Deck</span>
                  <span className="mt-1 block text-xs leading-5 text-white/80">Bring meaningful conversations to your table.</span>
                </span>
              </a>
            </div>
          </LandingReveal>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default WebLandingView;
